import React from "react";
import { Icon, Flex, Tile, Type } from "mesh-component-library";
import { CustomAddress, GoogleDestinationWithPlaceId } from "../../../../types";
import StarRating from "../../../../components/starrating";
import { CommunityText, Container, FullWidthFlex, HiddenDiv } from "./LocationCard.styles";
import titleCase from "../../../../utils/titleCase";
import { resolveLatLngValue } from "../../../../utils/resolveLatLngValue";

export type LocationCardProps = {
  destination: GoogleDestinationWithPlaceId;
  currentLocation: CustomAddress;
  handleNonTowToLocationClick: (payload) => void;
  handleTowToLocationClick: (payload) => void;
  service: google.maps.places.PlacesService;
  towToEnabled: boolean;
  showSponsoredLocation: boolean;
};

export const LocationCard = ({
  destination,
  currentLocation,
  handleNonTowToLocationClick,
  handleTowToLocationClick,
  service,
  towToEnabled,
  showSponsoredLocation
}: LocationCardProps) => {
  const { name, formatted_phone_number, hoursToDisplay, towInfo, rating, reviews, vicinity } = destination;

  const handleClick = () => {
    if (towToEnabled) {
      const origin = {
        lat: resolveLatLngValue(currentLocation.geometry.location.lat),
        lng: resolveLatLngValue(currentLocation.geometry.location.lng),
      };
      let query = destination.name + ", " + titleCase(destination.vicinity);
      handleTowToLocationClick({ query, service, origin });
    } else {
      const origin = {
        lat: resolveLatLngValue(currentLocation.geometry.location.lat),
        lng: resolveLatLngValue(currentLocation.geometry.location.lng),
      };
      const payload = {service, placeId: destination.place_id, origin}
      handleNonTowToLocationClick(payload);
    }
  };

  const getReviewCount = () =>
    (reviews?.length ? reviews.length.toString() : destination.user_ratings_total.toString()) || "";

  return (
    <Container data-testid="locationCard" onClick={handleClick}>
      <Tile borderLeft="gray">
        {/* Name, Address, Phone Number, Action Button  */}
        <Tile.Head
          actionButton={{
            text: <Icon icon="chevron" rotation={180} />,
            // @ts-ignore ActionButton type from mesh-component-library doesn't have this listed, but it is a valid prop.
            // github issue https://github.allstate.com/Mesh/Component-Library/issues/3586
            isHollow: true,
            isPrimary: false,
            ["data-testid"]: "locationCard-actionButton",
          }}
          title={
            <>
              {!!destination.isPreferredDealer && showSponsoredLocation && (
                <Type size="xs" align="left" variant="extraLight">
                  Sponsored
                </Type>
              )}
              <Type size="xl" weight="bold" variant="dark" align="left" data-testid="locationCard-name">
                {name}
              </Type>
            </>
          }
          variant="white"
          hasBorder={false}
          subtitle={
            <Type align="left" data-testid="locationCard-address">
              {vicinity} {formatted_phone_number}
            </Type>
          }
        />
        {/* Hours, distance, rating, cost */}
        <Tile.Body>
          <Flex direction="column">
            <FullWidthFlex alignItems="stretch" direction="row" flex="auto" justifyContent="between" wrap="nowrap">
              <Type align="left" size="2xs" weight="thin" variant="extraLight" data-testid="locationCard-hours">
                {hoursToDisplay}
              </Type>
              <Type size="2xs" weight="thin" variant="extraLight" data-testid="locationCard-distance">
                {towInfo.distanceFromCurrentLocation} {towInfo.unitsShorthand}
              </Type>
            </FullWidthFlex>
            <FullWidthFlex alignItems="stretch" direction="row" flex="auto" justifyContent="between" wrap="nowrap">
              {!!rating ? (
                <CommunityText align="left" size="2xs" data-testid="locationCard-rating">
                  <StarRating
                    starRating={destination.rating.toString()}
                    baseColor="orange"
                    reviews={getReviewCount()}
                  />
                </CommunityText>
              ) : (
                <HiddenDiv data-testid="locationCard-hiddenDiv" />
              )}
              <Type size="2xs" weight="bold" variant="dark" data-testid="locationCard-cost">
                ${towInfo.totalTowCost}
              </Type>
            </FullWidthFlex>
          </Flex>
        </Tile.Body>
      </Tile>
    </Container>
  );
};
